import { Download, FileText, Info, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type {
  AssistantTurn,
  EscalationOutcome,
  GenerationResult,
  IngestedAsset,
  SocialAdSet,
  SpecCorrection,
} from "@/lib/api";
import { cn } from "@/lib/utils";

import { BriefConfirm } from "./brief-confirm";
import { ClarificationChoices } from "./clarification-choices";
import { CopyDraftPanel } from "./copy-draft";
import { DeckPlanReview } from "./deck-plan-review";
import { DeckRevisionReview } from "./deck-revision-review";
import { OutcomeBanner } from "./outcome-banner";
import { PageChoice } from "./page-choice";
import { ReadAs } from "./read-as";
import { type FormatConfirmation, SocialAdPlanReview } from "./social-ad-plan";
import { SocialAdResults } from "./social-ad-results";

interface TurnCardProps {
  turn: AssistantTurn;
  /** Set once a page choice has produced a localized PDF. */
  pageResult: GenerationResult | undefined;
  /** Set once a format choice has rendered the ads, before the turn reloads. */
  socialAds: SocialAdSet | undefined;
  escalationOutcome: EscalationOutcome | undefined;
  onConfirmDeck: () => void;
  /** Rebuild the deck this turn proposed edits to, with only those edits. */
  onConfirmRevision: () => void;
  /** Ask the same question again with one or more controls set by hand. */
  onCorrect: (corrections: SpecCorrection) => void;
  onConfirmPages: (pages: number[]) => void;
  /** Which creative route to render, with which photograph and which sizes. */
  onConfirmFormats: (confirmation: FormatConfirmation) => void;
  onRebuildAsDeck: () => void;
  /** Send one of the clarification options as the next request, verbatim. */
  onChooseClarification: (option: string) => void;
  onConfirmEscalation: (confirmed: boolean, note: string) => void;
  isConfirmingDeck: boolean;
  isConfirmingRevision: boolean;
  isAsking: boolean;
  isConfirmingPages: boolean;
  isConfirmingFormats: boolean;
  isConfirmingEscalation: boolean;
}

/**
 * One exchange: what was asked, what the composer decided, and what it needs.
 *
 * The outcome drives what appears below the message — an asset to take, a deck
 * to download, a page grid to choose from, or a brief to approve.
 */
export function TurnCard({
  turn,
  pageResult,
  socialAds,
  escalationOutcome,
  onConfirmDeck,
  onConfirmRevision,
  onCorrect,
  onConfirmPages,
  onConfirmFormats,
  onRebuildAsDeck,
  onChooseClarification,
  onConfirmEscalation,
  isConfirmingDeck,
  isConfirmingRevision,
  isAsking,
  isConfirmingPages,
  isConfirmingFormats,
  isConfirmingEscalation,
}: TurnCardProps) {
  const deck = turn.result ?? undefined;
  const artifact = pageResult ?? deck;
  // Freshly rendered this session, or read back off a turn the history
  // remembered — the ads look the same either way.
  const ads = socialAds ?? turn.social_ads ?? undefined;
  const revision = turn.deck_revision ?? undefined;
  // Revision-shaped, but it named no slide the composer could place. That reads
  // as a question rather than as a proposal, down to the banner.
  const revisionUnresolved = Boolean(revision) && (revision?.edits ?? []).length === 0;
  // Empty on every turn that is not an ambiguous request — and on every turn
  // stored before the field existed, which carries nothing at all. Both read as
  // "no choice to offer", so the plain clarification keeps rendering as it was.
  const clarificationOptions = turn.clarification_options ?? [];

  return (
    <article className="space-y-3">
      {/* What the requestor asked, echoed back so the thread reads as a conversation. */}
      <div className="flex justify-end">
        <p className="bg-surface-strong text-foreground max-w-[80%] rounded-2xl rounded-br-sm px-4 py-2.5 text-sm">
          {turn.request_text}
        </p>
      </div>

      <div className="border-border bg-card space-y-3 rounded-2xl rounded-bl-sm border p-4 shadow-sm">
        <OutcomeBanner
          outcome={turn.outcome}
          tier={turn.tier}
          awaitingBuild={
            (Boolean(turn.deck_plan) && !deck) ||
            (Boolean(turn.social_ad_plan) && !ads) ||
            turn.awaiting === "revision_confirmation"
          }
          escalationKind={turn.escalation_draft?.kind ?? undefined}
          revisionUnresolved={revisionUnresolved}
          hasClarificationChoices={clarificationOptions.length > 0}
        />

        <p className="text-foreground text-sm leading-6">{stripMarkdownBold(turn.message)}</p>

        {/* Directly under the message, ahead of the reasoning — unlike every
            other body here, these are the answers to the sentence immediately
            above them, and a collapsed "why this" row between a question and
            its answers reads as a change of subject. */}
        {turn.outcome === "needs_clarification" && clarificationOptions.length > 0 && (
          <ClarificationChoices
            options={clarificationOptions}
            onChoose={onChooseClarification}
            isBusy={isAsking}
          />
        )}

        <details className="group">
          <summary className="text-muted hover:text-foreground flex cursor-pointer items-center gap-1.5 text-xs">
            <Info className="h-3 w-3" />
            Why this, and what it searched
          </summary>
          <div className="text-muted mt-2 space-y-2 text-xs leading-5">
            <p>{turn.rationale}</p>
            <p className="text-foreground/80">{turn.interpretation}</p>

            {/* The controls sit with the reasoning rather than above the
                answer: a market that was read correctly — most of the time —
                does not need four chips between the message and the file. */}
            <ReadAs turn={turn} onCorrect={onCorrect} isBusy={isAsking} />
            {(turn.candidates ?? []).length > 0 && (
              <ul className="space-y-1">
                {(turn.candidates ?? []).slice(0, 4).map((candidate) => (
                  <li key={candidate.asset.id} className="flex gap-2">
                    <span className="text-foreground/70 w-10 shrink-0 tabular-nums">
                      {candidate.score.toFixed(2)}
                    </span>
                    <span className="min-w-0">
                      <span className="text-foreground/90 font-medium">
                        {candidate.asset.filename}
                      </span>{" "}
                      — {candidate.why}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {(turn.unmatched_phrases ?? []).length > 0 && (
              <p>Not yet understood: {(turn.unmatched_phrases ?? []).join(", ")}</p>
            )}
          </div>
        </details>

        {/* Outcome 1 — an approved asset already answers it. */}
        {turn.outcome === "asset_as_is" && turn.asset && (
          <AssetResult asset={turn.asset} href={turn.asset_download_url ?? undefined} />
        )}

        {/* Outcome 2 — the plan is proposed first; building waits for a yes. */}
        {turn.outcome === "generate_deck" && turn.deck_plan && !deck && (
          <DeckPlanReview
            plan={turn.deck_plan}
            onApprove={onConfirmDeck}
            isBuilding={isConfirmingDeck}
          />
        )}

        {/* Outcome 3 — fixed layout, so the requestor chooses the pages. */}
        {turn.outcome === "propose_pdf_pages" && turn.document && (
          <PageChoice
            turn={turn}
            onConfirm={onConfirmPages}
            onRebuildAsDeck={onRebuildAsDeck}
            isBusy={isConfirmingPages}
            done={pageResult !== undefined}
          />
        )}

        {/* Outcome 4 — one photo, several shapes; the formats are the choice,
            and the renders replace the picker once they exist. */}
        {turn.outcome === "generate_social_ads" &&
          (ads ? (
            <SocialAdResults ads={ads} />
          ) : turn.social_ad_plan && turn.awaiting === "format_selection" ? (
            <SocialAdPlanReview
              plan={turn.social_ad_plan}
              onConfirm={onConfirmFormats}
              isBuilding={isConfirmingFormats}
            />
          ) : null)}

        {/* Outcome 5 — edits to the deck this conversation already built. Only
            the named slides move, so the readback is the proposal. */}
        {turn.outcome === "revise_deck" && revision && !deck && (
          <DeckRevisionReview
            revision={revision}
            awaiting={turn.awaiting}
            onConfirm={onConfirmRevision}
            isRebuilding={isConfirmingRevision}
          />
        )}

        {/* Outcome 6 — the deliverable is the words, not a file. They arrive
            finished, so there is nothing to approve: the panel is the answer,
            and the .docx below it is only there for forwarding. */}
        {turn.outcome === "write_copy" && turn.copy_draft && (
          <CopyDraftPanel draft={turn.copy_draft} />
        )}

        {/* Outcome 7 — needs the source file; ask before briefing Strategic Comms. */}
        {turn.outcome === "escalate_source_edit" && turn.escalation_draft && (
          <BriefConfirm
            draft={turn.escalation_draft}
            outcome={escalationOutcome}
            onConfirm={onConfirmEscalation}
            isBusy={isConfirmingEscalation}
          />
        )}

        {/* Outcomes 2 and 3 both end in one file. A set of ads is several, and
            they are shown as previews above rather than as a row claiming a
            slide count it does not have. */}
        {artifact && turn.outcome !== "generate_social_ads" && <ArtifactRow artifact={artifact} />}
      </div>
    </article>
  );
}

function AssetResult({ asset, href }: { asset: IngestedAsset; href: string | undefined }) {
  return (
    <div className="border-border flex items-center gap-3 rounded-xl border p-2.5">
      {asset.thumbnail_url ? (
        <img
          src={asset.thumbnail_url}
          alt=""
          className="bg-surface h-16 w-16 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <span className="bg-surface text-muted flex h-16 w-16 shrink-0 items-center justify-center rounded-lg">
          <FileText className="h-5 w-5" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-foreground truncate text-sm font-semibold">{asset.filename}</p>
        <p className="text-muted text-xs">
          {[
            asset.kind,
            asset.page_count ? `${asset.page_count} pages` : null,
            asset.width && asset.height ? `${asset.width}×${asset.height}` : null,
            asset.detected_language,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      {href && (
        <Button asChild variant="outline" size="sm">
          <a href={href} download>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Take it
          </a>
        </Button>
      )}
    </div>
  );
}

function ArtifactRow({ artifact }: { artifact: GenerationResult }) {
  const flagged = artifact.translation_summary.strings_needing_review ?? 0;
  return (
    <div
      className={cn(
        "border-velux/20 bg-velux-tint/40 flex flex-wrap items-center gap-3 rounded-xl border p-3",
      )}
    >
      <Sparkles className="text-velux h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-foreground truncate text-sm font-semibold">{artifact.filename}</p>
        <p className="text-muted text-xs">
          {/* A translated Word file has neither slides nor kept pages, and read
              the page count anyway it said "0 pages". Each kind is asked for
              the count it actually has. */}
          {artifact.output_kind === "pptx"
            ? `${(artifact.slides ?? []).length} slides · editable in PowerPoint`
            : artifact.work_mode === "write_copy"
              ? // Written copy is also a .docx, but nothing was localized in
                // place — claiming a preserved layout would describe a file
                // that has no original to preserve.
                "Word document · the copy, ready to forward"
              : artifact.output_kind === "docx"
                ? "Word document · layout preserved"
                : `${(artifact.pages_kept ?? []).length} pages · layout preserved`}
          {" · "}
          {artifact.translation_summary.strings_translated} strings to{" "}
          {artifact.spec.target_language}
          {flagged > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-outcome-escalated ml-1 cursor-help underline decoration-dotted">
                  {flagged} to check
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Flagged for a human read — the translation may not fit its original box, or came
                from the stub provider rather than a live translation.
              </TooltipContent>
            </Tooltip>
          )}
        </p>
      </div>
      <Button asChild size="sm">
        <a href={artifact.download_url} download>
          <Download className="mr-1.5 h-3.5 w-3.5" />
          Download .{artifact.output_kind}
        </a>
      </Button>
    </div>
  );
}

/**
 * The backend emphasises filenames with `**…**`. Rendering raw markdown would
 * mean trusting server text as HTML, so strip the markers instead.
 */
function stripMarkdownBold(text: string): string {
  return text.replace(/\*\*/g, "");
}
